import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

export type IncomeTransactionDiscardTarget = "payroll" | "non_payroll";

@Injectable()
export class StatementsService {
  constructor(private http: HttpClientService) {}

  public updateTransaction(
    id: number,
    category: string | null,
    accepted = true,
    amount?: number,
    real_amount?: number,
    consider_payroll_amount?: number,
    extra_payroll_amount_type?: number,
    extra_payroll_amount_detection?: boolean,
    statement_id?: number
  ): Observable<any> {
    const params = {
      transaction: {
        category,
        accepted,
        amount,
        real_amount,
        consider_payroll_amount,
        extra_payroll_amount_type,
        extra_payroll_amount_detection,
        statement_id,
      },
    };

    return this.http
      .put(`/api/statement_transaction/${id}`, params)
      .pipe(tap((_) => console.log("StatementsService.updateTransaction: Ok.")));
  }

  public updateUnbanReason(id: number, unban_reason: string): Observable<any> {
    return this.http
      .put(`/api/statement_transaction/update_unban_reason/${id}`, { unban_reason })
      .pipe(tap((_) => console.log("StatementsService.updateUnbanReason: Ok.")));
  }

  public masiveUpdateUncategorizedTransactions(app_id, category = "pending"): Observable<any> {
    const params = { app_id, category };
    return this.http
      .put(`/api/statement_transaction/masive_update_uncategorized`, params)
      .pipe(tap((_) => console.log("StatementsService.masiveUpdateUncategorizedTransactions: Ok.")));
  }

  public masiveUpdateTransactions(app_id, ids, category, previous_category, accepted = true): Observable<any> {
    const params = {
      app_id,
      ids,
      transaction: { category, accepted },
      previous_category,
      category,
    };
    return this.http
      .put(`/api/statement_transaction/masive_update`, params)
      .pipe(tap((_) => console.log("StatementsService.masiveUpdateTransactions: Ok.")));
  }

  public getBalanceGraphData(id: number, clabe: string): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/statements/balance_graph?clabe=${clabe}`)
      .pipe(tap((_) => console.log("StatementsService.getBalanceGraphData: Ok.")));
  }

  public getTransactionSummary(id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/statements/transaction_summary`)
      .pipe(tap((_) => console.log("StatementsService.getTransactionSummary: Ok.")));
  }

  public createTransaction(
    statement_id: number,
    movement: string,
    concept: string,
    amount: number,
    real_amount: number,
    category: string,
    accepted: boolean,
    transaction_type: string,
    transaction_date: string
  ): Observable<any> {
    const params: {
      statement_id: number;
      movement: string;
      concept?: string;
      amount: number;
      real_amount: number;
      category: string;
      accepted: boolean;
      transaction_type: string;
      transaction_date: string;
      balance: number;
    } = {
      statement_id,
      movement,
      amount,
      real_amount,
      category,
      accepted,
      transaction_type,
      transaction_date,
      balance: 0,
    };

    if (concept != null && concept !== "") {
      params.concept = concept;
    }

    return this.http
      .post(`/api/statement_transaction/`, params)
      .pipe(tap((_) => console.log("StatementsService.createTransaction: Ok.")));
  }

  public getTransactions(statement_id: number): Observable<any> {
    return this.http
      .get(`/api/statements/${statement_id}/transactions`)
      .pipe(tap((_) => console.log("StatementsService.getTransaction: Ok.")));
  }

  public getTraditionalStatement(app_id: number): Observable<any> {
    const options = {
      params: {
        analysis_method: "traditional",
        serializer: "ForTraditional",
      },
    };

    return this.http
      .get(`/api/applications/${app_id}/statements`, options)
      .pipe(tap((_) => console.log("StatementsService.getTraditionalStatement: Ok")));
  }

  public getManualStatement(app_id: number): Observable<any> {
    const options = {
      params: {
        analysis_method: "manual",
      },
    };

    return this.http
      .get(`/api/applications/${app_id}/statements`, options)
      .pipe(tap((_) => console.log("StatementsService.getManualStatement: Ok")));
  }

  public getUniqueStatements(app_id: number, analysis_method: string): Observable<any> {
    const options = {
      params: {
        analysis_method,
      },
    };

    return this.http
      .get(`/api/applications/${app_id}/statements/unique`, options)
      .pipe(tap((_) => console.log("StatementsService.getUniqueStatements: Ok")));
  }

  public getManualStatementByMonth(
    app_id: number,
    month: number,
    analysis_method = ["manual", "traditional"]
  ): Observable<any> {
    const options = {
      params: {
        analysis_method,
        month: month.toString(),
      },
    };

    return this.http
      .get(`/api/applications/${app_id}/statements/by_month`, options)
      .pipe(tap((_) => console.log("StatementsService.getManualStatementByMonth: Ok")));
  }

  public getAllTransactionsByApplication(
    app_id: number,
    analysis_method = ["manual", "traditional", "automatic"]
  ): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/statements/transactions`, { params: { analysis_method } })
      .pipe(tap((_) => console.log("StatementsService.getAllTransactionsByApplication: Ok")));
  }

  public getAllQualityFactors(): Observable<any> {
    return this.http
      .get(`/api/statements/quality_factors`)
      .pipe(tap((_) => console.log("StatementsService.getAllQualityFactors: Ok")));
  }

  public getStatementAccounts(app_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/statements/accounts`)
      .pipe(tap((_) => console.log("StatementsService.getAccounts: Ok")));
  }

  public createStatement(
    application_id: number,
    month: number,
    analysis_method: string,
    average: number,
    income_total: number,
    income_count: number,
    expense_total: number,
    expense_count: number,
    total: number,
    initial_balance: number,
    ending_balance: number,
    aditional_data: any,
    start_date: Date,
    end_date: Date
  ): Observable<any> {
    const params = {
      application_id,
      number: month,
      analysis_method,
      average,
      income_total,
      income_count,
      expense_total,
      expense_count,
      total,
      initial_balance,
      ending_balance,
      aditional_data,
      start_date,
      end_date,
    };
    return this.http
      .post(`/api/statements`, params)
      .pipe(tap((_) => console.log("StatementsService.createStatement: Ok")));
  }

  public getPendingTransactionsByApplication(
    app_id: number,
    analysis_method = ["manual", "traditional", "automatic"]
  ): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/statements/transactions/pendings`, { params: { analysis_method } })
      .pipe(tap((_) => console.log("StatementsService.getPendingTransactionsByApplication: Ok")));
  }

  public updateStatement(application_id: number, month: number, aditional_data: any): Observable<any> {
    const params = {
      application_id,
      number: month,
      aditional_data,
    };
    return this.http
      .put(`/api/statements/${application_id}`, params)
      .pipe(tap((_) => console.log("StatementsService.updateStatement: Ok")));
  }

  public swapStatements(statement_id: number, index: number): Observable<any> {
    return this.http
      .post(`/api/statements/${statement_id}/swap`, { index })
      .pipe(tap((_) => console.log("StatementsService.updateStatement: Ok")));
  }

  public deleteStatements(application_id, analysis_method = ["traditional"]): Observable<any> {
    return this.http
      .delete(`/api/applications/${application_id}/statements`, { params: { analysis_method } })
      .pipe(tap((_) => console.log("StatementsService.deleteStatements: Ok")));
  }

  public deleteTransaction(transaction_id: number): Observable<any> {
    return this.http
      .delete(`/api/statement_transaction/${transaction_id}`)
      .pipe(tap((_) => console.log("StatementsService.deleteTransaction: Ok")));
  }

  public getBanxicoParams(id: number): Observable<any> {
    return this.http
      .get(`/api/statement_transaction/banxico_params?id=${id}`)
      .pipe(tap((_) => console.log("StatementsService.getBanxicoParams: Ok.")));
  }

  public getBanxicoValidate(id: number): Observable<any> {
    return this.http
      .get(`/api/statement_transaction/banxico_validate?id=${id}`)
      .pipe(tap((_) => console.log("StatementsService.getBanxicoValidate: Ok.")));
  }

  public getBanxicoValidateManual(
    tipoCriterio: string,
    fecha: string,
    criterio: string,
    emisor: string,
    receptor: string,
    cuenta: string,
    monto: string
  ): Observable<any> {
    const options = {
      params: {
        tipoCriterio,
        fecha,
        criterio,
        emisor,
        receptor,
        cuenta: cuenta ?? "",
        monto,
      },
    };
    return this.http
      .get(`/api/statement_transaction/banxico_validate_manual`, options)
      .pipe(tap((_) => console.log("StatementsService.getBanxicoValidateManual: Ok.")));
  }

  public postBanxicoCepResult(
    id: number,
    body: {
      banxico_cep_ok: boolean;
      banxico_cep_duration: number;
      banxico_cep_params: {
        tipoCriterio: string;
        fecha: string;
        criterio: string;
        emisor: string;
        receptor: string;
        cuenta: string;
        receptorParticipante: string;
        monto: string;
        captcha: string;
        tipoConsulta: string;
      };
      banxico_cep_response: {
        ok: boolean;
        status: number;
        json: Record<string, unknown>;
        json_raw: Record<string, unknown>;
        error?: string | null;
        error_team?: { title: string; msg: string | null } | null;
      };
    }
  ): Observable<any> {
    return this.http
      .post(`/api/statement_transaction/banxico_cep_result/${id}`, body)
      .pipe(tap((_) => console.log("StatementsService.postBanxicoCepResult: Ok.")));
  }

  public createIncomeTransactionDiscard(
    application_id: number,
    target: IncomeTransactionDiscardTarget
  ): Observable<unknown> {
    return this.http.post("/api/statement_transaction/discards", {
      application_id,
      discard: { target },
    });
  }
}
